export interface GetActionBarBottomOffsetParams {
  bottomOffset?: string;
  isNavbarVisible: boolean;
  isNative: boolean;
  bottomNavbarHeight: string;
}

export interface ActionBarBottomOffset {
  bottom: string | number;
  paddingBottom: string | undefined;
}

/**
 * Computes where a fixed/absolute `AppBottomActionBar` should sit so it never
 * overlaps the bottom navbar, and whether it still needs to own the native
 * safe-area inset itself.
 *
 * - An explicit `bottomOffset` always wins (caller has already accounted for
 *   the navbar, e.g. the site-unit order bar).
 * - When the navbar is visible, the bar sits directly above it; the navbar
 *   already reserves the safe-area inset, so the bar does not add its own.
 * - When the navbar is hidden (or there is no provider, e.g. onboarding), the
 *   bar sits at the very bottom and owns the safe-area inset itself.
 */
export const getActionBarBottomOffset = ({
  bottomOffset,
  isNavbarVisible,
  isNative,
  bottomNavbarHeight,
}: GetActionBarBottomOffsetParams): ActionBarBottomOffset => {
  if (bottomOffset !== undefined) {
    return {
      bottom: bottomOffset,
      paddingBottom: isNative ? 'var(--safe-area-inset-bottom)' : undefined,
    };
  }

  if (isNavbarVisible) {
    return {
      bottom: isNative
        ? `calc(${bottomNavbarHeight} + var(--safe-area-inset-bottom))`
        : bottomNavbarHeight,
      paddingBottom: undefined,
    };
  }

  return {
    bottom: 0,
    paddingBottom: isNative ? 'var(--safe-area-inset-bottom)' : undefined,
  };
};
